import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { GET_MANAGER_ORDERS_URL, GET_MANAGER_DAILY_MENU_URL } from "../../../features/UrlHelper";
import { useOrdersRealtime, matchesOrderScope } from "../../../context/RealtimeContext";

// ─── useOverviewStats ─────────────────────────────────────────────────────────
// Fetchuje objednávky pre dátumový rozsah
// (startDateStr/endDateStr = "YYYY-MM-DD").
// Filtrovanie podľa trasy (routeId = "all" | "<id>") prebieha klient-side,
// takže zmena trasy nevyvolá nové sieťové volanie.
//
// Zdrojom pravdy pre predajnosť sú výhradne objednávky (order_items),
// nie aktuálny stav katalógu jedál. Zmazané jedlá sa tak správne zobrazujú
// v historických štatistikách.
//
// Vracia:
//   orders      – pole objednávok (filtrované podľa trasy, pre zoznam + zoradenie)
//   loading     – boolean (iba pre prvé načítanie a zmenu rozsahu)
//   refreshing  – boolean (tichý refresh na pozadí, netreba naň gateovať obsah)
//   error       – string | null
//   refetch     – funkcia na vyžiadanie dát; refetch({ silent: true }) nechá
//                 loading aj error nedotknuté
//
// Obrazovka sa obnovuje sama pri každej zmene objednávky v zvolenom rozsahu –
// odber je priamo tu, takže OverviewSection nepotrebuje žiadnu zmenu.
//   kpi         – { totalRevenue, ordersCount, totalDebt, pickupRevenue, averageOrderValue }
//   salesByType – { SOUP, MENU, SIDE_DISH, DRINK, DESSERT } → pole { id, name, code, limit, sold, revenue }
//   hourlyData  – [{ hour: "HH:00", orders, revenue }] zoradené podľa hodiny

export function useOverviewStats(startDateStr, endDateStr, routeId) {
  const [rawOrders, setRawOrders]         = useState([]);
  const [dailyMenuItems, setDailyMenuItems] = useState([]);
  const [loading, setLoading]             = useState(false);
  const [refreshing, setRefreshing]       = useState(false);
  const [error, setError]                 = useState(null);
  // Seq ref namiesto pôvodného `cancelled` flagu: ten sa neskladá s
  // imperatívnym refetchom, ktorý potrebuje realtime odber.
  const seqRef = useRef(0);

  // ── Sieťové volania (znova pri zmene rozsahu) ───────────────────────────────
  const load = useCallback(async ({ silent = false } = {}) => {
    const seq = ++seqRef.current;
    if (silent) setRefreshing(true);
    else {
      setLoading(true);
      setError(null);
    }


    try {
      const [ordersRes, menuRes] = await Promise.all([
        axios.get(GET_MANAGER_ORDERS_URL(startDateStr, endDateStr)),
        axios.get(GET_MANAGER_DAILY_MENU_URL(startDateStr, endDateStr)),
      ]);
      if (seq !== seqRef.current) return;
      setRawOrders(ordersRes.data);
      setDailyMenuItems(menuRes.data);
    } catch (err) {
      if (seq !== seqRef.current) return;
      // Pri tichom refreshi sa error NEnastavuje: nahradiť fungujúci prehľad
      // hláškou "Nepodarilo sa načítať dáta" kvôli jednému výpadku na pozadí
      // by bolo horšie než ukázať mierne staršie čísla.
      if (!silent) setError("Nepodarilo sa načítať dáta. Skúste stránku obnoviť.");
    } finally {
      if (seq !== seqRef.current) return;
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [startDateStr, endDateStr]);

  useEffect(() => {
    load();
  }, [load]);

  useOrdersRealtime({
    filter: (evt) => matchesOrderScope(evt, { dateFrom: startDateStr, dateTo: endDateStr }),
    onChange: () => load({ silent: true }),
    // Rozsahový dopyt je ťažší než denný, preto sa debouncuje viac.
    debounceMs: 800,
  });

  // ── Filtrovanie objednávok podľa trasy (bez re-fetch) ──────────────────────
  const orders = useMemo(
    () =>
      routeId === "all"
        ? rawOrders
        : rawOrders.filter((o) => String(o.route_id) === routeId),
    [rawOrders, routeId]
  );

  // ── KPI štatistiky (z route-filtrovaných objednávok) ───────────────────────
  const kpi = useMemo(() => {
    const active = orders.filter((o) => o.status !== "CANCELLED");
    // Tržba: iba CASH a DEBT.
    //
    // Odber na prevádzke sa do nej NERÁTA — má vlastnú kartu hneď vedľa.
    // Sú to dva rôzne kanály a majiteľ ich porovnáva; keby boli v jednom
    // čísle a druhá karta bola jeho časťou, sčítaním by si tržbu zdvojil.
    const cashOrDebtOrders = active.filter((o) => o.payment_type === "CASH" || o.payment_type === "DEBT");
    const totalRevenue = cashOrDebtOrders
      .filter((o) => o.delivery_type !== "PICKUP")
      .reduce((s, o) => s + Number(o.total_price || 0), 0);
    const ordersCount  = active.length;
    
    // Dlh (DEBT)
    const debtOrders = active.filter((o) => o.payment_type === "DEBT");
    const totalDebt = debtOrders.reduce((s, o) => s + Number(o.total_price || 0), 0);
    const debtOrdersCount = debtOrders.length;
    
    // Faktúra (INVOICE)
    const invoiceOrders = active.filter((o) => o.payment_type === "INVOICE");
    const totalInvoice = invoiceOrders.reduce((s, o) => s + Number(o.total_price || 0), 0);
    const invoiceOrdersCount = invoiceOrders.length;
    
    // Tržba na prevádzke (R52): to, čo si zákazníci vyzdvihli sami.
    // Počíta sa rovnako ako celková tržba — CASH a DEBT — len z objednávok
    // s odberom na prevádzke. Spolu s `totalRevenue` tvoria celok, neprekrývajú sa.
    const pickupOrders = active.filter((o) => o.delivery_type === "PICKUP");
    const pickupRevenue = pickupOrders
      .filter((o) => o.payment_type === "CASH" || o.payment_type === "DEBT")
      .reduce((s, o) => s + Number(o.total_price || 0), 0);
    const pickupOrdersCount = pickupOrders.length;

    // Priemer ide cez VŠETKO, čo zákazníci kúpili — rozvoz aj pult.
    const averageOrderValue = ordersCount > 0 ? (totalRevenue + pickupRevenue) / ordersCount : 0;
    return {
      totalRevenue, ordersCount, totalDebt, debtOrdersCount,
      totalInvoice, invoiceOrdersCount, pickupRevenue, pickupOrdersCount, averageOrderValue,
    };
  }, [orders]);

  // ── Predajnosť denného menu (z VŠETKÝCH objednávok – menu nie je per-trasa) ─
  // Zdrojom predaných kusov sú výhradne objednávky. Snapshot dáta (name,
  // item_type, menu_code) pochádzajú priamo z položky objednávky, takže
  // zmazané jedlá sa správne zobrazujú v historických štatistikách.
  // Limit sa dopĺňa z aktuálneho denného menu (ak položka stále existuje).
  const salesByType = useMemo(() => {
    // Index limitov podľa daily_menu_id (len aktívne položky katalógu)
    const limitMap = {};
    for (const dm of dailyMenuItems) {
      limitMap[dm.id] = Number(dm.planned_daily_limit) || 0;
    }

    // Agregujeme podľa daily_menu_id – kľúče zo snapshot dát v order_items
    const aggregated = {};

    for (const order of rawOrders) {
      if (order.status === "CANCELLED") continue;
      for (const item of order.items || []) {
        const id  = item.daily_menu_id;
        if (!id) continue;
        const qty = Number(item.quantity || 0);
        if (!aggregated[id]) {
          aggregated[id] = {
            id,
            name:    item.name      || "",
            code:    item.menu_code || null,
            typeKey: (item.item_type || "").toUpperCase(),
            limit:   limitMap[id] ?? 0,
            sold:    0,
            revenue: 0,
          };
        }
        aggregated[id].sold    += qty;
        aggregated[id].revenue += qty * Number(item.price || 0);
      }
    }

    const result = { SOUP: [], MENU: [], SIDE_DISH: [], DRINK: [], DESSERT: [] };
    for (const entry of Object.values(aggregated)) {
      const { typeKey, ...rest } = entry;
      const group = result[typeKey] ?? (result[typeKey] = []);
      group.push(rest);
    }

    return result;
  }, [rawOrders, dailyMenuItems]);

  // ── Hodinový graf (z route-filtrovaných objednávok) ────────────────────────
  const hourlyData = useMemo(() => {
    const map = {};
    for (const order of orders) {
      if (order.status === "CANCELLED") continue;
      const h   = new Date(order.created_at).getHours();
      const key = `${String(h).padStart(2, "0")}:00`;
      if (!map[key]) map[key] = { hour: key, orders: 0, revenue: 0 };
      map[key].orders  += 1;
      map[key].revenue += Number(order.total_price || 0);
    }
    return Object.values(map).sort((a, b) => a.hour.localeCompare(b.hour));
  }, [orders]);

  return { orders, loading, refreshing, error, kpi, salesByType, hourlyData, refetch: load };
}
